import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { Shell } from '@/components/layout/Shell';
import { Empty, Spinner, ToastProvider } from '@/components/ui/core';
import { lazy, Suspense } from 'react';
import { InvitePage, LoginPage, MfaPage } from '@/features/auth/Login';
import { DashboardPage } from '@/features/dashboard/DashboardPage';

// Secondary screens load on demand: the dashboard stays fast to display.
const page = <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  lazy(async () => ({ default: (await load())[name] }));
const admin = () => import('@/features/admin/AdminPages');
const AccountPage = page(admin, 'AccountPage');
const AuditPage = page(admin, 'AuditPage');
const RoadmapPage = page(admin, 'RoadmapPage');
const SettingsPage = page(admin, 'SettingsPage');
const UsersPage = page(admin, 'UsersPage');
const AlertsPage = page(() => import('@/features/alerts/AlertsPage'), 'AlertsPage');
const backups = () => import('@/features/backups/BackupsPages');
const BackupsPage = page(backups, 'BackupsPage');
const BackupDetailPage = page(backups, 'BackupDetailPage');
const ConnectorsPage = page(() => import('@/features/connectors/ConnectorsPage'), 'ConnectorsPage');
const onboarding = () => import('@/features/onboarding/Onboarding');
const NewProjectPage = page(onboarding, 'NewProjectPage');
const SetupWizard = page(onboarding, 'SetupWizard');
const ProjectPage = page(() => import('@/features/projects/ProjectPage'), 'ProjectPage');
const restores = () => import('@/features/restores/Restore');
const RestoreWizard = page(restores, 'RestoreWizard');
const RestoreReportPage = page(restores, 'RestoreReportPage');
const SetupPage = page(() => import('@/features/setup/SetupWizard'), 'SetupWizard');
const quality = () => import('@/features/quality/QualityPages');
const DesignPreview = page(() => import('@/features/design/DesignPreview'), 'DesignPreview');
const SecurityOverviewPage = page(quality, 'SecurityOverviewPage');
const IncidentsPage = page(quality, 'IncidentsPage');
import { ApiError } from '@/lib/api';
import { SessionProvider, useSession } from '@/lib/session';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

function RequireAuth() {
  const { me, loading } = useSession();
  if (loading) return <Spinner />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.mfa === 'required' || me.mfa === 'setup_required') return <Navigate to="/mfa" replace />;
  return (
    <Suspense fallback={<Spinner />}>
      <Outlet />
    </Suspense>
  );
}

function NotFound() {
  return <Empty title="Page introuvable">Cette adresse ne correspond à aucun écran de la console.</Empty>;
}

const router = createBrowserRouter([
  ...(import.meta.env.DEV ? [{ path: '/__design', element: <Shell />, children: [{ index: true, element: <DesignPreview /> }] }] : []),
  { path: '/login', element: <LoginPage /> },
  { path: '/mfa', element: <MfaPage /> },
  { path: '/invite/:token', element: <InvitePage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Shell />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'projects/new', element: <NewProjectPage /> },
          { path: 'projects/:slug', element: <ProjectPage /> },
          { path: 'projects/:slug/setup', element: <SetupWizard /> },
          { path: 'projects/:slug/restore', element: <RestoreWizard /> },
          { path: 'restores/:id', element: <RestoreReportPage /> },
          { path: 'backups', element: <BackupsPage /> },
          { path: 'backups/:id', element: <BackupDetailPage /> },
          { path: 'alerts', element: <AlertsPage /> },
          { path: 'incidents', element: <IncidentsPage /> },
          { path: 'security', element: <SecurityOverviewPage /> },
          { path: 'connectors', element: <ConnectorsPage /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'audit', element: <AuditPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'account', element: <AccountPage /> },
          { path: 'roadmap', element: <RoadmapPage /> },
          { path: 'setup', element: <SetupPage /> },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <SessionProvider>
          <RouterProvider router={router} />
        </SessionProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
